import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css, useTheme } from 'styled-components';
import styled from 'styled-components';

import { ORDER_TABLE_TABS } from 'src/common/constants';
import { changeScrollBehavior } from 'src/common/helpers';
import { Button, Checkbox } from 'src/components/core';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { Tab, Tabs } from 'src/components/core/tabs';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import * as actions from 'src/setup/redux/slices/spotSlice';
import { ActiveOrder, ConditionalOrder } from 'src/types';

import { ActiveOrdersTable } from './desktop/activeOrdersTable';
import { ConditionalOrdersTable } from './desktop/conditionalOrdersTable';
import { OrderHistoryTable } from './desktop/orderHistoryTable';
import { TradeHistoryTable } from './desktop/tradeHistoryTable';
import { ActiveOrdersCard } from './mobile/activeOrdersCard';
import { ConditionalOrdersCard } from './mobile/conditionalOrdersCard';
import { OrderHistoryCard } from './mobile/orderHistoryCard';
import { TradeHistoryCard } from './mobile/tradeHistoryCard';
import { ErrorBoundary } from '../../common/error/ErrorBoundary';
import { CancelAllOrdersConfirmModal } from '../modals/cancelAllOrdersConfirmModal';

export const OrderTableContainer = styled.div<{ isExpand: boolean }>`
    ${(props) =>
        props.isExpand
            ? css`
                  display: none;
              `
            : css`
                  display: block;
              `}
    ${({ theme: { colors, typography, breakpoints } }) => `
    width: 100%;
    .order-tabs-header {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
    .order-tabs-header::-webkit-scrollbar {
        display: none;
    }
    .rc-table-container {
        .rc-table-cell {
            min-width: max-content;
        }
        .rc-table-cell > span {
            min-width: max-content;
        }
    }
    .tabs {
        .tab {
            min-width: max-content;
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            padding: 12px 12px;
            &.tab-active {
                font-weight: ${typography.weight.medium};
                padding: 12px 12px 10px;
            }
        }
    }
    @media (max-width: ${breakpoints.md}) {
        .tabs {
            & > .tab:not(:last-of-type) {
                margin-right: 32px;
            }
            .tab {
                font-size: ${typography.size.desktop.paragraph.p2};
                font-weight: ${typography.weight.normal};
                line-height: ${typography.lineHeight[4]};
                padding: 10px 0px;
                &.tab-active {
                    padding: 10px 0px 8px;
                }
            }
        }
    }
    & .no-record {
        & p {
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            color: ${colors.text.tertiary};
        }
    }
    & .logout-user-message {
        & p {
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            color: ${colors.text.tertiary};
        }
    }

    & .show-all-orders {
        min-width: max-content;
        margin: 0 24px;
        & label {
            color: ${colors.text.tertiary};
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            @media (max-width: ${breakpoints.md}) {
                font-size: ${typography.size.desktop.paragraph.p2};
                font-weight: ${typography.weight.normal};
                line-height: ${typography.lineHeight[4]};
            }
        }
        & .button {
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            padding: 0;
            margin-left: 16px;

        }
    }
    `}
`;

const AllOrderLink = styled.a`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        cursor: pointer;
        margin-left: 16px;
    `}
`;

export const StyledCheckbox: any = styled(Checkbox)<{ isChecked: boolean }>`
    ${(props) =>
        !props.isChecked &&
        css`
            ${({ theme: { colors } }) => `
        :before {
            border: 1px solid ${colors.text.tertiary};
            background-color: ${colors.background.secondary.v3};
        }
        `}
        `}
`;
interface OrderTableProps {
    isExpand: boolean;
}
const OrderTable: FC<OrderTableProps> = ({ isExpand }) => {
    const { t } = useTranslation();
    const { isMobile, colors } = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [activeOrderCount, setActiveOrderCount] = useState(0);
    const [conditionalOrderCount, setConditionalOrderCount] = useState(0);
    const [isShowAllActiveOrder, setIsShowAllActiveOrder] = useState(true);
    const [isShowAllConditionalOrder, setIsShowAllConditionalOrder] = useState(true);
    const [isShowAllHistoryOrder, setIsShowAllHistoryOrder] = useState(true);
    const [isShowAllTradeHistoryOrder, setIsShowAllTradeHistoryOrder] = useState(true);
    const [isActiveOrderConfirmModalOpen, setIsActiveOrderConfirmModalOpen] = useState(false);
    const [isConditionalOrderConfirmModalOpen, setIsConditionalOrderConfirmModalOpen] =
        useState(false);

    const { error, loading } = useAppSelector((state) => state.spot);
    const symbolId = useAppSelector((state) => state.spot.currentSymbol.symbolId);
    const { isAuthenticated } = useAuth();

    const activeOrders: Array<ActiveOrder> = useAppSelector((state) => state.spot.activeOpenOrders);
    const conditionalOrders: Array<ConditionalOrder> = useAppSelector(
        (state) => state.spot.conditionalOpenOrders,
    );

    const fetchActiveOpenOrdersError = error?.fetchActiveOpenOrders !== undefined;
    const fetchActiveOpenOrdersLoading = loading?.fetchActiveOpenOrders;
    const fetchConditionalOpenOrdersError = error?.fetchConditionalOpenOrders !== undefined;
    const fetchConditionalOpenOrdersLoading = loading?.fetchConditionalOpenOrders;
    const fetchActiveTradeOrdersError = error?.fetchActiveTradeOrders !== undefined;
    const fetchActiveTradeOrdersLoading = loading?.fetchActiveTradeOrders;
    const fetchConditionalTradeOrdersError = error?.fetchConditionalTradeOrders !== undefined;
    const fetchConditionalTradeOrdersLoading = loading?.fetchConditionalTradeOrders;
    const fetchMyTradesError = error?.fetchMyTrades !== undefined;
    const fetchMyTradesLoading = loading?.fetchMyTrades;

    const fetchSpotActiveOpenOrders = () => {
        dispatch(
            actions.fetchActiveOpenOrders(
                'checked=false&order_type=LIMIT&firstReq=true&type=LIMIT&limit=100&from_order_id=',
            ),
        );
    };
    const fetchSpotConditionalOpenOrders = () => {
        dispatch(
            actions.fetchConditionalOpenOrders(
                'checked=false&order_type=STOP&firstReq=true&type=STOP&limit=100&from_order_id=',
            ),
        );
    };
    const fetchSpotActiveTradeOrders = () => {
        dispatch(
            actions.fetchActiveTradeOrders(
                'checked=false&order_type=&firstReq=true&limit=100&from_order_id=&type=',
            ),
        );
    };
    const fetchSpotConditionalTradeOrders = () => {
        dispatch(
            actions.fetchConditionalTradeOrders(
                'checked=false&order_type=LIMIT&firstReq=true&function=historyOrder&api=futures_history_order&limit=100&from_trade_id=',
            ),
        );
    };
    const fetchSpotMyTrades = () => {
        dispatch(actions.fetchMyTrades(`limit=100&s=getOrders&from_trade_id=0`));
    };

    const dispatch = useAppDispatch();
    // TODO: Need to improve the api calling for individual tabs.
    useEffect(() => {
        // TODO: Fix according to design when user is login then should only call the api else render the logout user design
        if (isAuthenticated) {
            fetchSpotActiveOpenOrders();
            fetchSpotConditionalOpenOrders();
            fetchSpotActiveTradeOrders();
            fetchSpotConditionalTradeOrders();
            fetchSpotMyTrades();
        }
    }, [dispatch, isAuthenticated, symbolId]);

    // TODO: Need to improve the api calling for individual tabs.
    useEffect(() => {
        // TODO: Fix according to design when user is login then should only call the api else render the logout user design
        if (isAuthenticated && orderTableTabs[activeTab].label === ORDER_TABLE_TABS.ORDER_HISTORY) {
            fetchSpotActiveTradeOrders();
            fetchSpotConditionalTradeOrders();
        }
        if (isAuthenticated && orderTableTabs[activeTab].label === ORDER_TABLE_TABS.TRADE_HISTORY) {
            fetchSpotMyTrades();
        }
    }, [dispatch, activeTab]);

    const getActiveOrderCount = useCallback(() => {
        const filteredActiveOrders = isShowAllActiveOrder
            ? activeOrders
            : activeOrders.filter((activeOrder) => activeOrder.symbolId === symbolId);
        return filteredActiveOrders.length;
    }, [activeOrders, isShowAllActiveOrder, symbolId]);

    const getConditionalOrderCount = useCallback(() => {
        const filteredConditionalOrders = isShowAllConditionalOrder
            ? conditionalOrders
            : conditionalOrders?.filter(
                  (conditionalOrder) => conditionalOrder.symbolId === symbolId,
              );
        return filteredConditionalOrders?.length ?? 0;
    }, [conditionalOrders, isShowAllConditionalOrder, symbolId]);

    useEffect(() => {
        setActiveOrderCount(getActiveOrderCount());
        setConditionalOrderCount(getConditionalOrderCount());
    }, [
        getActiveOrderCount,
        getConditionalOrderCount,
        setActiveOrderCount,
        setConditionalOrderCount,
    ]);

    const switchTab = (tabIndex: number) => {
        //TODO: Need to improve, currently storing as per required due to implementation in web plus code.
        const trading_index =
            orderTableTabs[tabIndex].label === ORDER_TABLE_TABS.TRADE_HISTORY ? '2' : '1';
        window.localStorage.setItem(`trading_index`, trading_index);
        setActiveTab(tabIndex);
    };

    const orderTableTabs = [
        {
            label: ORDER_TABLE_TABS.ACTIVE_ORDERS,
            icon: (
                <span style={{ color: colors.primary, marginLeft: '5px' }}>{activeOrderCount}</span>
            ),
            iconPlacement: 'right',
            desktopContent: <ActiveOrdersTable isShowAll={isShowAllActiveOrder} />,
            mobileContent: <ActiveOrdersCard isShowAll={isShowAllActiveOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchActiveOpenOrdersError}
                        loading={fetchActiveOpenOrdersLoading}
                        retryAction={fetchSpotActiveOpenOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllActiveOrder}
                        isDefault={true}
                        label={t('show_all_spot_pairs')}
                        onChange={setIsShowAllActiveOrder}
                        isChecked={isShowAllActiveOrder}
                    />
                    {activeOrderCount > 0 && (
                        <Button
                            variant="text"
                            className="button-text"
                            color={colors.primary}
                            onClick={() => {
                                setIsActiveOrderConfirmModalOpen(true);
                            }}
                        >
                            {t('cancel_all')}
                        </Button>
                    )}
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.CONDITIONAL_ORDERS,
            icon: (
                <span style={{ color: colors.primary, marginLeft: '5px' }}>
                    {conditionalOrderCount}
                </span>
            ),
            desktopContent: <ConditionalOrdersTable isShowAll={isShowAllConditionalOrder} />,
            mobileContent: <ConditionalOrdersCard isShowAll={isShowAllConditionalOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchConditionalOpenOrdersError}
                        loading={fetchConditionalOpenOrdersLoading}
                        retryAction={fetchSpotConditionalOpenOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllConditionalOrder}
                        isDefault={true}
                        label={t('show_all_spot_pairs')}
                        onChange={setIsShowAllConditionalOrder}
                        isChecked={isShowAllConditionalOrder}
                    />
                    {conditionalOrderCount > 0 && (
                        <Button
                            variant="text"
                            className="button-text"
                            color={colors.primary}
                            onClick={() => {
                                setIsConditionalOrderConfirmModalOpen(true);
                            }}
                        >
                            {t('cancel_all')}
                        </Button>
                    )}
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.ORDER_HISTORY,
            desktopContent: <OrderHistoryTable isShowAll={isShowAllHistoryOrder} />,
            mobileContent: <OrderHistoryCard isShowAll={isShowAllHistoryOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchActiveTradeOrdersError}
                        loading={fetchActiveTradeOrdersLoading}
                        retryAction={fetchSpotActiveTradeOrders}
                    >
                        <ErrorBoundary
                            hasError={fetchConditionalTradeOrdersError}
                            loading={fetchConditionalTradeOrdersLoading}
                            retryAction={fetchSpotConditionalTradeOrders}
                        />
                    </ErrorBoundary>
                    <StyledCheckbox
                        value={isShowAllHistoryOrder}
                        isDefault={true}
                        label={t('show_all_spot_pairs')}
                        onChange={setIsShowAllHistoryOrder}
                        isChecked={isShowAllHistoryOrder}
                    />
                    <AllOrderLink href={APP_URLS.ALL_SPOT_ORDERS}>{t('all_orders')}</AllOrderLink>
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.TRADE_HISTORY,
            desktopContent: <TradeHistoryTable isShowAll={isShowAllTradeHistoryOrder} />,
            mobileContent: <TradeHistoryCard isShowAll={isShowAllTradeHistoryOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchMyTradesError}
                        loading={fetchMyTradesLoading}
                        retryAction={fetchSpotMyTrades}
                    />
                    <StyledCheckbox
                        value={isShowAllTradeHistoryOrder}
                        isDefault={true}
                        label={t('show_all_spot_pairs')}
                        onChange={setIsShowAllTradeHistoryOrder}
                        isChecked={isShowAllTradeHistoryOrder}
                    />
                    <AllOrderLink href={APP_URLS.ALL_SPOT_ORDERS}>{t('all_orders')}</AllOrderLink>
                </>
            ),
        },
    ];
    return (
        <OrderTableContainer isExpand={isExpand} className="order-table">
            <>
                <FlexContainer
                    className="order-tabs-header"
                    styles={{
                        background: `${colors.background.secondary.v3}`,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: `1px solid ${colors.border.light}`,
                        height: '39px',
                        width: '100%',
                        overflowX: 'scroll',
                        overflowY: 'hidden',
                    }}
                >
                    <Tabs value={activeTab} onChange={switchTab} className={'order-table-tabs'}>
                        {orderTableTabs.map((tab, index) => {
                            return (
                                <Tab
                                    value={index}
                                    key={index}
                                    label={t(tab.label)}
                                    icon={tab.icon}
                                    iconPlacement={'right'}
                                />
                            );
                        })}
                    </Tabs>
                    {orderTableTabs[activeTab].showAllCheckbox && (
                        <FlexContainer className="show-all-orders">
                            {orderTableTabs[activeTab].showAllCheckbox}
                        </FlexContainer>
                    )}
                </FlexContainer>
                <FlexContainer
                    styles={{
                        alignItems: 'center',
                        padding: isMobile ? '0 16px' : '0 0 0 20px',
                        width: '100%',
                        minHeight: isMobile ? '210px' : '300px',
                        background: `${colors.background.primary}`,
                    }}
                >
                    {isMobile
                        ? orderTableTabs[activeTab].mobileContent
                        : orderTableTabs[activeTab].desktopContent}
                </FlexContainer>
                <CancelAllOrdersConfirmModal
                    isOpen={isActiveOrderConfirmModalOpen}
                    orderType={'ACTIVE'}
                    symbolId={isShowAllActiveOrder ? 'ALL' : symbolId}
                    handleClose={() => {
                        changeScrollBehavior(!isActiveOrderConfirmModalOpen);
                        setIsActiveOrderConfirmModalOpen(!isActiveOrderConfirmModalOpen);
                    }}
                />
                <CancelAllOrdersConfirmModal
                    isOpen={isConditionalOrderConfirmModalOpen}
                    orderType={'CONDITIONAL'}
                    symbolId={isShowAllConditionalOrder ? 'ALL' : symbolId}
                    handleClose={() => {
                        changeScrollBehavior(!isConditionalOrderConfirmModalOpen);
                        setIsConditionalOrderConfirmModalOpen(!isConditionalOrderConfirmModalOpen);
                    }}
                />
            </>
        </OrderTableContainer>
    );
};

export { OrderTable };
